import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AddressAutofill } from "@mapbox/search-js-react";
import {
  Car, ShieldCheck, CheckCircle2, Loader2, Gauge, MapPin, ArrowLeft, ArrowRight,
  BadgeCheck, AlertTriangle, TrendingUp, Crown, Truck, Landmark, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation, Trans } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PhotoUploader } from "@/components/PhotoUploader";
import { DocUploader } from "@/components/DocUploader";
import { HostAgreementView, useHostAgreement } from "@/components/host/HostAgreementView";
import { Seo } from "@/components/Seo";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const VEHICLE_TYPES = ["Economy", "Sedan", "Hatchback", "SUV", "Truck", "Pickup Truck", "Minivan", "Cargo Van", "Luxury"];

function Stepper({ step }) {
  const { t } = useTranslation();
  const STEPS = [t("onboarding.stepAbout"), t("onboarding.stepCar"), t("onboarding.agreementStep")];
  return (
    <div className="flex items-center gap-3 mb-10" data-testid="host-stepper">
      {STEPS.map((s, i) => (
        <div key={s} className="flex items-center gap-3">
          <div className={`flex items-center gap-2 text-sm font-semibold ${i <= step ? "text-blue-600" : "text-slate-400"}`}>
            <span className={`grid place-items-center w-7 h-7 rounded-full text-xs ${i < step ? "bg-blue-600 text-white" : i === step ? "bg-blue-100 text-blue-700 ring-2 ring-blue-600" : "bg-slate-100 text-slate-400"}`}>
              {i < step ? <CheckCircle2 size={15} /> : i + 1}
            </span>
            <span className="hidden sm:block">{s}</span>
          </div>
          {i < STEPS.length - 1 && <span className={`w-8 h-0.5 rounded ${i < step ? "bg-blue-600" : "bg-slate-200"}`} />}
        </div>
      ))}
    </div>
  );
}

export default function HostOnboarding() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const agreementDoc = useHostAgreement();

  const EARNINGS = [
    { icon: Car, tier: t("onboarding.tierEconomy"), range: "$800–$1,200", sub: t("onboarding.perMo") },
    { icon: Truck, tier: t("onboarding.tierSUV"), range: "$1,100–$1,600", sub: t("onboarding.perMo") },
    { icon: Crown, tier: t("onboarding.tierPremium"), range: "$1,500+", sub: t("onboarding.perMo") },
  ];

  const [step, setStep] = useState(0);
  const [fullName, setFullName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState("");

  const [vin, setVin] = useState("");
  const [decoding, setDecoding] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [trim, setTrim] = useState("");
  const [odometer, setOdometer] = useState("");
  const [params] = useSearchParams();
  // Prefill weekly rate from the earnings calculator (?daily=) or ?weekly= so an
  // applicant who used the calculator lands with their price already set.
  const [weeklyRate, setWeeklyRate] = useState(() => {
    const w = params.get("weekly");
    const d = params.get("daily");
    if (w && Number(w) > 0) return String(Math.round(Number(w)));
    if (d && Number(d) > 0) return String(Math.round(Number(d) * 7));
    return "350";
  });
  const [category, setCategory] = useState("Sedan");
  const [photos, setPhotos] = useState([]);
  const [insurance, setInsurance] = useState("");
  const [registration, setRegistration] = useState("");
  const [addressText, setAddressText] = useState("");

  const [bank, setBank] = useState({ holder: "", routing: "", account: "", type: "checking" });
  const setB = (k) => (e) => setBank((b) => ({ ...b, [k]: e.target.value }));

  const [signature, setSignature] = useState(user?.name || "");
  const [agreed, setAgreed] = useState(false);
  const [insuranceAck, setInsuranceAck] = useState(false);
  const [background, setBackground] = useState("");
  const [vehicleCount, setVehicleCount] = useState(1);
  const [hostingInterest, setHostingInterest] = useState("self");
  const [preferredLang, setPreferredLang] = useState(i18n?.language?.startsWith("pt") ? "pt" : "en");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const fail = (e) => toast.error(formatApiError(e.response?.data?.detail));

  const decode = async () => {
    if (!vin.trim()) return toast.error(t("onboarding.enterVin"));
    setDecoding(true); setVehicle(null);
    try {
      const r = await api.get(`/host/decode-vin/${vin.trim()}`);
      setVehicle(r.data);
      setTrim(r.data.trim || "");
    } catch (e) { fail(e); } finally { setDecoding(false); }
  };

  const handleRetrieve = (res) => {
    const f = res?.features?.[0];
    if (!f) return;
    const p = f.properties || {};
    setAddressText(p.full_address || p.place_name || [p.address_line1, p.address_level2, p.address_level1, p.postcode].filter(Boolean).join(", "));
  };

  const odoNum = Number(odometer);
  const odoValid = odometer !== "" && odoNum >= 0;
  const routingDigits = (bank.routing || "").replace(/\D/g, "");
  const accountDigits = (bank.account || "").replace(/\D/g, "");
  const bankValid = bank.holder.trim() && routingDigits.length === 9 && accountDigits.length >= 4;

  const step0Ok = fullName.trim() && email.trim() && phone.trim();
  const step1Ok = vehicle && vehicle.year_qualifies && odoValid && addressText.trim()
    && photos.length >= 5 && insurance && registration && bankValid;
  const canSubmit = agreed && insuranceAck && signature.trim() && !submitting;

  const submit = async () => {
    if (!canSubmit) return toast.error(t("onboarding.agreementError"));
    setSubmitting(true);
    try {
      const r = await api.post("/host/apply", {
        full_name: fullName, email, phone, address: addressText,
        vin, year: vehicle.year, make: vehicle.make, model: vehicle.model,
        trim, odometer: odoNum, weekly_rate: Number(weeklyRate) || 350,
        category, images: photos, insurance_url: insurance, registration_url: registration,
        bank_account_holder: bank.holder, bank_routing: routingDigits,
        bank_account_number: accountDigits, bank_account_type: bank.type,
        agreement_signed: true, signature_name: signature, certified: true,
        hosting_background: background, vehicle_count: Number(vehicleCount) || 1,
        hosting_interest: hostingInterest, preferred_language: preferredLang,
        insurance_acknowledged: insuranceAck,
      });
      setDone(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { fail(e); } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div data-testid="host-success" className="bg-white min-h-screen pt-32 pb-24 px-5 sm:px-8">
        <div className="max-w-xl mx-auto text-center">
          <span className="grid place-items-center w-16 h-16 rounded-full bg-green-50 text-green-600 mx-auto"><CheckCircle2 size={34} /></span>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-6">{t("onboarding.underReviewTitle")}</h1>
          <p className="text-slate-500 mt-4 leading-relaxed">{done.message} {t("onboarding.underReviewSub")}</p>
          <div className="flex gap-3 justify-center mt-8">
            <button onClick={() => navigate("/host/dashboard")} data-testid="host-success-portal" className="bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700 transition-colors">{t("onboarding.goToHostPortal")}</button>
            <Link to="/fleet" data-testid="host-success-fleet" className="border-2 border-slate-200 text-slate-700 px-7 py-3 rounded-full font-semibold hover:bg-slate-50 transition-colors">{t("onboarding.browseFleet")}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="host-onboarding-page" className="bg-white min-h-screen pt-32 pb-24 px-5 sm:px-8">
      <Seo noindex title="List Your Car | Belo Rentals" path="/host" />
      <div className="max-w-2xl mx-auto">
        <p className="label-caps text-blue-600">{t("onboarding.becomeHost")}</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-slate-900 mt-3 tracking-tight font-bold">{t("onboarding.listYourCar")}</h1>
        <p className="text-slate-500 mt-4">{t("onboarding.intro")}</p>

        <div data-testid="host-earnings" className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {EARNINGS.map((e) => (
            <div key={e.tier} className="bg-white border border-slate-100 rounded-2xl shadow-card p-5">
              <span className="grid place-items-center w-10 h-10 rounded-xl bg-blue-50 text-blue-600"><e.icon size={18} /></span>
              <p className="font-heading text-2xl font-bold text-slate-900 mt-3">{e.range}<span className="text-slate-400 text-sm font-normal">{e.sub}</span></p>
              <p className="text-slate-500 text-sm mt-0.5">{e.tier}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 bg-white rounded-2xl shadow-card p-7 sm:p-9">
          <Stepper step={step} />

          {step === 0 && (
            <div className="space-y-6" data-testid="host-step-1">
              <p className="text-slate-500 text-sm -mt-2">{t("onboarding.contactHint")}</p>
              <div>
                <span className="label-caps text-slate-400 block mb-2">{t("onboarding.fullName")}</span>
                <input value={fullName} onChange={(e) => setFullName(e.target.value)} data-testid="host-fullname" className="belo-input" placeholder={t("onboarding.fullNamePlaceholder")} />
              </div>
              <div>
                <span className="label-caps text-slate-400 block mb-2">{t("onboarding.email")}</span>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="host-email" className="belo-input" placeholder={t("onboarding.emailPlaceholder")} />
              </div>
              <div>
                <span className="label-caps text-slate-400 block mb-2">{t("onboarding.mobilePhone")}</span>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="host-phone" className="belo-input" placeholder={t("onboarding.phonePlaceholder")} />
              </div>

              <div className="border-t border-slate-100 pt-5 space-y-4" data-testid="host-background">
                <p className="label-caps text-slate-400">Your hosting background</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-sm text-slate-600">Are you hosting elsewhere?</span>
                    <select value={background} onChange={(e) => setBackground(e.target.value)} data-testid="host-bg-status" className="belo-input mt-1.5">
                      <option value="">Select…</option>
                      <option value="current_turo">Current Turo host</option>
                      <option value="former_turo">Former Turo host</option>
                      <option value="other_marketplace">Host on another marketplace</option>
                      <option value="new">New to vehicle hosting</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-sm text-slate-600">How many vehicles?</span>
                    <input type="number" min="1" value={vehicleCount} onChange={(e) => setVehicleCount(Math.max(1, Number(e.target.value) || 1))} data-testid="host-bg-count" className="belo-input mt-1.5" />
                  </label>
                  <label className="block">
                    <span className="text-sm text-slate-600">Hosting preference</span>
                    <select value={hostingInterest} onChange={(e) => setHostingInterest(e.target.value)} data-testid="host-bg-interest" className="belo-input mt-1.5">
                      <option value="self">Self-managed</option>
                      <option value="managed">Managed hosting</option>
                      <option value="both">Interested in both</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-sm text-slate-600">Preferred language</span>
                    <select value={preferredLang} onChange={(e) => setPreferredLang(e.target.value)} data-testid="host-bg-language" className="belo-input mt-1.5">
                      <option value="en">English</option>
                      <option value="pt">Português (Brasil)</option>
                    </select>
                  </label>
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <button onClick={() => setStep(1)} disabled={!step0Ok} data-testid="host-next-1" className="flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:opacity-40">{t("onboarding.continue")} <ArrowRight size={16} /></button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6" data-testid="host-step-2">
              <div>
                <span className="label-caps text-slate-400 block mb-2">{t("onboarding.vehicleVin")}</span>
                <div className="flex gap-2">
                  <input value={vin} onChange={(e) => setVin(e.target.value.toUpperCase())} data-testid="host-vin" className="belo-input font-mono" placeholder={t("onboarding.vinPlaceholder")} maxLength={17} />
                  <button onClick={decode} disabled={decoding} data-testid="host-decode-vin" className="shrink-0 bg-slate-900 text-white px-6 rounded-xl text-sm font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50">{decoding ? <Loader2 size={16} className="animate-spin" /> : t("onboarding.decode")}</button>
                </div>
              </div>

              {vehicle && (
                <div data-testid="host-vehicle-result" className={`rounded-xl p-5 ${vehicle.year_qualifies ? "bg-green-50" : "bg-red-50"}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-heading text-lg font-bold text-slate-900">{vehicle.year} {vehicle.make} {vehicle.model}</p>
                      {vehicle.body_class && <p className="text-slate-500 text-sm">{vehicle.body_class}</p>}
                    </div>
                    {vehicle.year_qualifies ? (
                      <span className="inline-flex items-center gap-1.5 text-green-700 text-sm font-semibold"><BadgeCheck size={16} /> {t("onboarding.qualifies")}</span>
                    ) : (
                      <span data-testid="host-year-fail" className="inline-flex items-center gap-1.5 text-red-600 text-sm font-semibold"><AlertTriangle size={16} /> {t("onboarding.tooOld")}</span>
                    )}
                  </div>
                  {!vehicle.year_qualifies && <p className="text-red-600 text-sm mt-2">{t("onboarding.tooOldDetail", { years: vehicle.max_age_years, from: vehicle.current_year - vehicle.max_age_years })}</p>}
                  <div className="mt-4">
                    <span className="label-caps text-slate-400 block mb-2">{t("onboarding.trim")}</span>
                    <input value={trim} onChange={(e) => setTrim(e.target.value)} data-testid="host-trim" className="belo-input bg-white" placeholder={t("onboarding.trimPlaceholder")} />
                  </div>
                  {vehicle.year_qualifies && vehicle.est_monthly_low && (
                    <div className="mt-4 flex items-center gap-2 bg-white rounded-lg px-3 py-2.5 border border-green-100" data-testid="host-earnings-estimate">
                      <TrendingUp size={16} className="text-green-600" />
                      <span className="text-sm text-slate-700"><Trans i18nKey="onboarding.earnEstimate" values={{ low: vehicle.est_monthly_low.toLocaleString(), high: vehicle.est_monthly_high.toLocaleString() }} components={[<b className="text-slate-900" />]} /></span>
                    </div>
                  )}
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <span className="label-caps text-slate-400 block mb-2">{t("onboarding.odometer")}</span>
                  <input type="number" value={odometer} onChange={(e) => setOdometer(e.target.value)} data-testid="host-odometer" className="belo-input" placeholder={t("onboarding.odometerPlaceholder")} />
                  {odometer !== "" && !odoValid && <p className="text-red-600 text-xs mt-1.5" data-testid="host-odo-error">{t("onboarding.odoError")}</p>}
                </div>
                <div>
                  <span className="label-caps text-slate-400 block mb-2">{t("onboarding.weeklyRate")}</span>
                  <input type="number" value={weeklyRate} onChange={(e) => setWeeklyRate(e.target.value)} data-testid="host-weekly-rate" className="belo-input" placeholder="350" />
                </div>
              </div>

              <div>
                <span className="label-caps text-slate-400 block mb-2">{t("onboarding.vehicleType")}</span>
                <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="host-category" className="belo-input">
                  {VEHICLE_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div>
                <span className="label-caps text-slate-400 block mb-2">{t("onboarding.photos")} <span className="text-slate-300 normal-case font-normal">{t("onboarding.photosHint")}</span></span>
                <PhotoUploader value={photos} onChange={setPhotos} min={5} />
              </div>

              <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-5 space-y-4" data-testid="host-docs">
                <div className="flex items-center gap-2 text-slate-700">
                  <FileText size={16} className="text-blue-600" />
                  <span className="font-heading font-semibold text-slate-900">{t("onboarding.docsTitle")}</span>
                </div>
                <p className="text-slate-500 text-xs -mt-2">{t("onboarding.docsHint")}</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <DocUploader label={t("onboarding.insurance")} value={insurance} onChange={setInsurance} testid="host-insurance" />
                  <DocUploader label={t("onboarding.registration")} value={registration} onChange={setRegistration} testid="host-registration" />
                </div>
              </div>

              <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-5 space-y-4" data-testid="host-payout">
                <div className="flex items-center gap-2 text-slate-700">
                  <Landmark size={16} className="text-blue-600" />
                  <span className="font-heading font-semibold text-slate-900">{t("onboarding.payoutTitle")}</span>
                </div>
                <p className="text-slate-500 text-xs -mt-2">{t("onboarding.payoutHint")}</p>
                <div>
                  <span className="label-caps text-slate-400 block mb-2">{t("onboarding.accountHolder")}</span>
                  <input value={bank.holder} onChange={setB("holder")} data-testid="host-bank-holder" className="belo-input bg-white" placeholder="Jane Driver" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <span className="label-caps text-slate-400 block mb-2">{t("onboarding.routingNumber")}</span>
                    <input value={bank.routing} onChange={setB("routing")} data-testid="host-bank-routing" inputMode="numeric" maxLength={9} className="belo-input bg-white font-mono" placeholder="011000015" />
                  </div>
                  <div>
                    <span className="label-caps text-slate-400 block mb-2">{t("onboarding.accountNumber")}</span>
                    <input value={bank.account} onChange={setB("account")} data-testid="host-bank-account" inputMode="numeric" className="belo-input bg-white font-mono" placeholder="000123456789" />
                  </div>
                </div>
                <div>
                  <span className="label-caps text-slate-400 block mb-2">{t("onboarding.accountType")}</span>
                  <select value={bank.type} onChange={setB("type")} data-testid="host-bank-type" className="belo-input bg-white">
                    <option value="checking">{t("onboarding.checking")}</option>
                    <option value="savings">{t("onboarding.savings")}</option>
                  </select>
                </div>
              </div>

              <div>
                <span className="label-caps text-slate-400 block mb-2">{t("onboarding.address")}</span>
                <form onSubmit={(e) => e.preventDefault()} autoComplete="on">
                  {MAPBOX_TOKEN ? (
                    <AddressAutofill accessToken={MAPBOX_TOKEN} onRetrieve={handleRetrieve}>
                      <input autoComplete="address-line1" data-testid="host-address" className="belo-input" placeholder={t("onboarding.addressPlaceholder")} value={addressText} onChange={(e) => setAddressText(e.target.value)} />
                    </AddressAutofill>
                  ) : (
                    <input data-testid="host-address" className="belo-input" placeholder={t("onboarding.addressFallback")} value={addressText} onChange={(e) => setAddressText(e.target.value)} />
                  )}
                </form>
                {addressText && <p className="flex items-center gap-1.5 text-slate-600 text-sm mt-2" data-testid="host-address-confirm"><MapPin size={14} className="text-blue-600" /> {addressText}</p>}
              </div>

              <div className="flex justify-between pt-2">
                <button onClick={() => setStep(0)} data-testid="host-back-2" className="flex items-center gap-2 border-2 border-slate-200 text-slate-700 px-6 py-3 rounded-full font-semibold hover:bg-slate-50 transition-colors"><ArrowLeft size={16} /> {t("onboarding.back")}</button>
                <button onClick={() => setStep(2)} disabled={!step1Ok} data-testid="host-next-2" className="flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:opacity-40">{t("onboarding.continue")} <ArrowRight size={16} /></button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6" data-testid="host-step-3">
              <div className="bg-slate-50 rounded-xl p-5 space-y-3" data-testid="host-summary">
                <Row icon={Car} label={t("onboarding.vehicle")} value={`${vehicle?.year} ${vehicle?.make} ${vehicle?.model}${trim ? ` ${trim}` : ""}`} />
                <Row icon={Gauge} label={t("onboarding.odometer")} value={`${Number(odometer).toLocaleString()} mi`} />
                <Row icon={MapPin} label={t("onboarding.address")} value={addressText} />
                <Row icon={Landmark} label={t("onboarding.payoutTitle")} value={`•••• ${accountDigits.slice(-4)} (${bank.type})`} />
                <Row icon={ShieldCheck} label={t("onboarding.host")} value={`${fullName} · ${phone}`} />
                <div className="flex items-center justify-between pt-1 border-t border-slate-200">
                  <span className="text-slate-500 text-sm">{t("onboarding.weeklyRateLabel")}</span>
                  <span className="font-heading font-bold text-slate-900">{t("onboarding.perWeek", { rate: Number(weeklyRate) || 350 })}</span>
                </div>
              </div>

              <div>
                <h3 className="font-heading font-semibold text-slate-900">{t("onboarding.agreementTitle")}</h3>
                <p className="text-slate-500 text-sm mt-1 mb-3">{t("onboarding.agreementIntro")}</p>
                <HostAgreementView doc={agreementDoc} />
              </div>

              <div>
                <span className="label-caps text-slate-400 block mb-2">{t("onboarding.agreementSignLabel")}</span>
                <input value={signature} onChange={(e) => setSignature(e.target.value)} data-testid="host-signature" className="belo-input" placeholder={t("onboarding.fullNamePlaceholder")} />
              </div>

              <label className="flex items-start gap-3 cursor-pointer bg-blue-50/50 border border-blue-100 rounded-xl p-4" data-testid="host-agree-label">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="host-agree" className="mt-1 w-4 h-4 accent-blue-600" />
                <span className="text-slate-700 text-sm leading-relaxed">{t("onboarding.agreementAgree")}</span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer bg-amber-50 border border-amber-100 rounded-xl p-4" data-testid="host-insurance-ack-label">
                <input type="checkbox" checked={insuranceAck} onChange={(e) => setInsuranceAck(e.target.checked)} data-testid="host-insurance-ack" className="mt-1 w-4 h-4 accent-amber-600" />
                <span className="text-slate-700 text-sm leading-relaxed">
                  I understand that Belo Rentals does not provide insurance or physical-damage protection for my vehicle. I am responsible for confirming that my insurance permits rental or marketplace use and for handling claims involving my vehicle.
                </span>
              </label>

              <div className="flex justify-between pt-2">
                <button onClick={() => setStep(1)} data-testid="host-back-3" className="flex items-center gap-2 border-2 border-slate-200 text-slate-700 px-6 py-3 rounded-full font-semibold hover:bg-slate-50 transition-colors"><ArrowLeft size={16} /> {t("onboarding.back")}</button>
                <button onClick={submit} disabled={!canSubmit} data-testid="host-submit" className="flex items-center gap-2 bg-blue-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:opacity-40">{submitting ? <><Loader2 size={16} className="animate-spin" /> {t("onboarding.submitting")}</> : t("onboarding.submitListing")}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="flex items-center gap-2 text-slate-500 text-sm"><Icon size={15} /> {label}</span>
      <span className="text-slate-900 text-sm font-medium text-right">{value}</span>
    </div>
  );
}
